import React, { useState } from 'react';
import { Box, Grid, Theme, Typography } from '@mui/material';
import {
  AccessTime,
  CalendarMonth,
  CreditCard,
  Key,
} from '@mui/icons-material';
import { ButtonDefault } from '../../../shared/components/ButtonDefault';
import { createStyles, makeStyles } from '@mui/styles';
import {
  Elements,
  useStripe,
  useElements,
  CardNumberElement,
  CardCvcElement,
  CardExpiryElement,
} from '@stripe/react-stripe-js';
import { loadStripe } from '@stripe/stripe-js';
import { STRIPE_KEY } from '../../../shared/constans';
import { useAuth } from '../../auth/auth-hook';
import { CreatePaymentMethodData } from '@stripe/stripe-js/dist/stripe-js/payment-intents';
import {
  CurrentUserDocument,
  PaymentMethod,
  SubscriptionStatuses,
  useCreateStripePaymentMethodMutation,
} from '../../../shared/types/generated';

const useStyles = makeStyles<Theme>(() =>
  createStyles({
    icon: {
      color: '#999999',
      fontSize: '20px!important',
      paddingRight: '5px',
    },
    input: {
      padding: '8px 12px',
      border: '1px solid #c4c4c4',
      borderRadius: '4px',
      display: 'flex',
    },
    header: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      border: '1px solid #9CA5AC',
      padding: 10,
    },
    container: {
      borderBottom: '1px solid #9CA5AC',
      borderRight: '1px solid #9CA5AC',
      borderLeft: '1px solid #9CA5AC',
      borderRadius: '0 0 5px 5px',
      padding: 10,
    },
    button: {
      minWidth: '140px!important',
      fontFamily: 'oswald !important',
      textTransform: 'uppercase',
      fontWeight: '400 !important',
    },
  }),
);

const stripePromise = loadStripe(STRIPE_KEY);

type CheckoutFormProps = {
  onClose: () => void;
};

const CheckoutForm: React.FC<CheckoutFormProps> = ({ onClose }) => {
  const stripe = useStripe();
  const elements = useElements();
  const classes = useStyles();
  const [loading, setLoading] = React.useState(false);
  const { user, refetch } = useAuth();

  const [call, { loading: stripePaymentLoading }] =
    useCreateStripePaymentMethodMutation({
      onCompleted: () => {
        refetch();
        onClose();
      },
    });

  const subscription = user?.subscriptions?.find(
    (item) =>
      item?.payment_method === PaymentMethod.StripeCard &&
      item?.status === SubscriptionStatuses.Active,
  );

  const handleSubmit = async (): Promise<void> => {
    setLoading(true);
    const cardElement = elements?.getElement(CardNumberElement);
    if (!stripe || !elements || !cardElement) {
      // Stripe.js has not yet loaded.
      return;
    }

    const data: CreatePaymentMethodData = {
      type: 'card',
      card: cardElement,
    };
    const { error, paymentMethod } = await stripe.createPaymentMethod(data);

    if (error) {
      setLoading(false);
    } else {
      call({
        awaitRefetchQueries: true,
        refetchQueries: [CurrentUserDocument],
        variables: {
          data: {
            payment_method_id: paymentMethod?.id,
            customer_id: subscription?.customer_id,
          },
        },
      });
    }

    setLoading(false);
  };

  if (!subscription) return null;

  return (
    <>
      <Box className={classes.container}>
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <Box className={classes.input}>
              <CreditCard className={classes.icon} />
              <Box sx={{ flexGrow: 1 }}>
                <CardNumberElement
                  options={{
                    disableLink: true,
                    placeholder: 'Número de tarjeta',
                  }}
                />
              </Box>
            </Box>
          </Grid>
          <Grid item xs={12}>
            <Box className={classes.input}>
              <CalendarMonth className={classes.icon} />
              <Box sx={{ flexGrow: 1 }}>
                <CardExpiryElement
                  options={{ placeholder: 'Fecha de vencimiento' }}
                />
              </Box>
            </Box>
          </Grid>
          <Grid item xs={12}>
            <Box className={classes.input}>
              <Key className={classes.icon} />
              <Box sx={{ flexGrow: 1 }}>
                <CardCvcElement options={{ placeholder: 'CVV/CVC' }} />
              </Box>
            </Box>
          </Grid>
          <Grid item xs={12}>
            <ButtonDefault
              variant="contained"
              size="large"
              fullWidth
              isLoading={loading || stripePaymentLoading}
              onClick={handleSubmit}
              className={classes.button}
            >
              Guardar
            </ButtonDefault>
          </Grid>
        </Grid>
      </Box>
    </>
  );
};

export const StripeCardItem: React.FC = () => {
  const classes = useStyles();
  const { user } = useAuth();
  const [isOpen, setIsOpen] = useState(false);

  const subscription = user?.subscriptions?.length
    ? user?.subscriptions[0]
    : undefined;
  const card = user?.card;

  return (
    <>
      <Box
        className={classes.header}
        borderRadius={isOpen ? '5px 5px 0 0' : '5px'}
      >
        <Box display={'flex'} alignItems={'center'} color={'#707070'}>
          <Box display={'flex'}>
            <img
              alt="icon-Card"
              src={'/card-icon.svg'}
              width={20}
              height={20}
            />
          </Box>
          <Box ml={'15px'}>
            <Typography fontFamily={'manop'} fontWeight={700}>
              {card?.brand}{' '}
              {card?.card_number?.replaceAll('X', '*').split(' ', 4)}
            </Typography>
            <Box display={'flex'}>
              <AccessTime fontSize="small" />
              <Typography fontFamily={'manop'} ml={'5px'} fontSize={14}>
                Vence el {card?.expiration_month}/{card?.expiration_year}
              </Typography>
            </Box>
          </Box>
        </Box>
        {subscription?.status === 'ACTIVE' && (
          <Box>
            <ButtonDefault
              variant="contained"
              size="large"
              onClick={() => setIsOpen(!isOpen)}
              className={classes.button}
            >
              {!isOpen ? 'Actualizar' : 'Cerrar'}
            </ButtonDefault>
          </Box>
        )}
      </Box>
      {isOpen ? (
        <Elements stripe={stripePromise}>
          <CheckoutForm onClose={() => setIsOpen(false)} />
        </Elements>
      ) : null}
    </>
  );
};
